import { useEffect, useState, type RefObject } from "react";

import { hiddenBy, toLatest } from "../utils/scrollToLatest";
import styles from "./ScrollToLatest.module.css";

// A chip just above a conversation's pinned composer while its latest message is behind
// the bar or below the window; one click brings it to just above the bar.
export const ScrollToLatest = ({
  contentRef,
  barRef,
}: {
  /** The element whose children are the transcript and what follows it. */
  contentRef: RefObject<HTMLElement>;
  /** The pinned bar the chip sits in. */
  barRef: RefObject<HTMLElement>;
}) => {
  const [away, setAway] = useState(false);
  useEffect(() => {
    const content = contentRef.current;
    const bar = barRef.current;
    if (!content || !bar) return;
    const update = () => setAway(hiddenBy(content, bar) > 1);
    update();
    window.addEventListener("scroll", update, { passive: true });
    content.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    // A phone's keyboard opening pans and shrinks the visual viewport without a window
    // scroll or resize, which left the chip showing over a fully visible transcript.
    window.visualViewport?.addEventListener("scroll", update);
    window.visualViewport?.addEventListener("resize", update);
    // A turn arriving or a card opening grows the transcript without a scroll.
    const grows = new ResizeObserver(update);
    const watch = () => {
      grows.disconnect();
      grows.observe(bar);
      for (const child of Array.from(content.children)) grows.observe(child);
    };
    watch();
    const added = new MutationObserver(() => {
      watch();
      update();
    });
    added.observe(content, { childList: true });
    return () => {
      window.removeEventListener("scroll", update);
      content.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
      window.visualViewport?.removeEventListener("resize", update);
      grows.disconnect();
      added.disconnect();
    };
  }, [contentRef, barRef]);
  if (!away) return null;
  return (
    <button
      type="button"
      className={styles.chip}
      onClick={() => {
        const content = contentRef.current;
        const bar = barRef.current;
        if (content && bar) toLatest(content, bar, "smooth");
        // The chip goes once the scroll lands, so focus moves on to the composer beside
        // it - but not on a touch screen, where focusing the box opens the keyboard.
        if (!window.matchMedia("(pointer: coarse)").matches) {
          bar?.querySelector("textarea")?.focus({ preventScroll: true });
        }
      }}
    >
      <span aria-hidden>↓</span> Scroll to latest
    </button>
  );
};
